import { styled, type SxProps, type Theme } from "@mui/material/styles";

import { FRAME_BORDER_WIDTH } from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.constants";

export const PREVIEW_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flex: 1,
  minHeight: 0,
  gap: 1,
};

export const CONTROLS_SX: SxProps<Theme> = { display: "flex", alignItems: "center", gap: 1.5 };

export const SCREEN_SIZE_SELECT_SX: SxProps<Theme> = { width: 224 };

export const OPTION_LABEL_SX: SxProps<Theme> = { display: "flex", alignItems: "center", gap: 0.75 };

export const STAGE_SX: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  flex: 1,
  minHeight: 0,
  width: "100%",
  overflow: "hidden",
};

/** The skeleton sits on the page itself, so the frame keeps its border width but drops its border and fill. */
export const FrameBox = styled("div", {
  shouldForwardProp: (prop) => prop !== "frameWidth" && prop !== "isLoading",
})<{ frameWidth?: number; isLoading: boolean }>(({ theme, frameWidth, isLoading }) => ({
  position: "relative",
  width: frameWidth ?? "100%",
  maxWidth: "100%",
  height: "100%",
  overflow: "hidden",
  border: `${FRAME_BORDER_WIDTH}px solid ${isLoading ? "transparent" : theme.palette.divider}`,
  backgroundColor: isLoading ? "transparent" : theme.palette.background.paper,
}));

/**
 * The frame's own width is what the preview's media queries read, so a scaled-down Large frame keeps its
 * full breakpoint width and is shrunk visually instead.
 */
export const PreviewIframe = styled("iframe", {
  shouldForwardProp: (prop) =>
    prop !== "scale" && prop !== "frameWidth" && prop !== "frameHeight" && prop !== "isBuffering",
})<{ scale: number; frameWidth?: number; frameHeight?: number; isBuffering: boolean }>(
  ({ scale, frameWidth, frameHeight, isBuffering }) => ({
  display: "block",
  border: 0,
  width: frameWidth ?? "100%",
  height: frameHeight ?? "100%",
  transform: scale < 1 ? `scale(${scale})` : undefined,
  transformOrigin: "top left",
  // Hidden but still laid out, so the buffered document loads its stylesheets and fonts before it is shown.
  ...(isBuffering && { position: "absolute", top: 0, left: 0, visibility: "hidden" }),
  }),
);

export const SKELETON_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 4 };

export const SKELETON_SECTION_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 2.5 };

export const SKELETON_GROUP_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 1.5 };

/** The stage is the grey page itself, where the theme's grey-200 bar barely shows; Figma steps it up here. */
export const SKELETON_BAR_SX: SxProps<Theme> = (theme) => ({ backgroundColor: theme.palette.grey[300] });

export const ERROR_SX: SxProps<Theme> = (theme) => ({ p: 4, color: theme.palette.error.main });
